import { createColumnHelper } from '@tanstack/react-table';
import { formatDate, formatDateTime } from '@common/helpers';
import { IDocumentHistoryNote, IDocumentHistoryVersion } from './model/IDocumentHistoryVersion';
import DocumentStatus from '@common/components/DocumentStatus/DocumentStatus';
import { DocumentStatus as DocumentStatusType } from '@common/types';
import { Button, ButtonTypes } from '@common/components';
import { StyledLink } from '@common/components/HyperlinkFileInput/styles';
import { FileRoles } from '../../types';

export const prepareDocumentColumns = (onContentClick: (taskId: number) => void) => {
  const columnHelper = createColumnHelper<Omit<IDocumentHistoryVersion, 'expandableContent' | 'documentNotes'>>();

  return [
    columnHelper.accessor('title', {
      header: 'Title',
    }),
    columnHelper.accessor('content', {
      header: 'Content',
      cell: ({ row }) => {
        var value = row.original.content;
        return <Button type={ButtonTypes.TEXT} text={value} height={25} onClick={() => onContentClick(row.original.taskId)} />;
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => (
        <DocumentStatus
          value={info.getValue() as DocumentStatusType}
          conversionState={{
            isFailed: null,
            isInProgress: null,
          }}
        />
      ),
    }),
    columnHelper.accessor('version', {
      header: 'Version',
    }),
    columnHelper.accessor('regulatorApproval', {
      header: 'Regulator approval',
      cell: ({ getValue }) => (getValue() === null ? '-' : !!getValue() ? 'Yes' : 'No'),
    }),
    columnHelper.accessor('regulatorApprovalDate', {
      header: 'MHRA Approval date',
      cell: ({ getValue }) => {
        return formatDate(getValue());
      },
    }),
    columnHelper.accessor('approvedBy', {
      header: 'Approved by',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('approvedDate', {
      header: 'Approved date',
      cell: ({ getValue }) => {
        const value = getValue();

        return value ? formatDateTime(new Date(value)) : '-';
      },
    }),
    columnHelper.accessor('retiredBy', {
      header: 'Retired by',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('retiredDate', {
      header: 'Retired date',
      cell: ({ getValue }) => {
        const value = getValue();

        return value ? formatDateTime(new Date(value)) : '-';
      },
    }),
  ];
};

export const prepareDetailsColumns = (showAttachmentsColumn: boolean, taskId: number, onFileDownload?: (taskId: number, noteId: number, fileRole?: FileRoles) => void) => {
  const columnHelper = createColumnHelper<IDocumentHistoryNote>();
  return [
    columnHelper.accessor('description', {
      header: 'Description',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('notes', {
      header: 'Notes',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('from', {
      header: 'From',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('to', {
      header: 'To',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    columnHelper.accessor('date', {
      header: 'Date',
      cell: ({ getValue }) => {
        const value = getValue();

        return value ? formatDateTime(new Date(value)) : '-';
      },
    }),
    columnHelper.accessor('performedBy', {
      header: 'Performed by',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value : '-';
      },
    }),
    showAttachmentsColumn &&
    columnHelper.accessor('noteFiles', {
      header: 'Attachments',
      cell: ({ getValue, row }) => {
        const files = getValue();
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {files?.map((file, i) => (
              <StyledLink key={i} onClick={() => onFileDownload && onFileDownload(taskId, row.original.noteId, file.fileRole)}>
                {file.originalFileName}
              </StyledLink>
            ))}
          </div>
        );
      },
    }),
  ].filter((x) => x);
};
